import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import { DataGrid, GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { DataProcessRow, fetchDataProcessAdmin } from './helper';
import {
  COLOR_BACKGROUND_2,
  COLOR_GREY_200,
  COLOR_PRIMARY_1,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
} from 'src/utils/colors';
import { parseUtcSignupStringToDisplay, shortTzLabel } from '../adminDisplayTime';

type DataProcessProps = {
  adminPassword: string | null;
  displayTimeZone: string;
};

const TABLE_HEADER_BG = '#F1F5F9';
const TABLE_BG = '#FFFFFF';

type DataProcessPeriod = 'month' | 'year' | 'q1' | 'q2' | 'q3' | 'q4';

function yearFromMonthInput(monthYyyyMm: string): string {
  const y = String(monthYyyyMm || '').slice(0, 4);
  return /^\d{4}$/.test(y) ? y : String(new Date().getUTCFullYear());
}

function formatIntHuman(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US');
}

const STATUS_SUCCESS = '#16a34a';
const STATUS_FAILED = '#dc2626';
const STATUS_SCHEDULED = '#64748b';
const STATUS_NEUTRAL = '#94a3b8';

function statusTooltipContent(row: DataProcessRow, statusKey: string): React.ReactNode {
  const full = row.full_total_count;
  const thr = row.success_fill_percent_threshold;
  const hasCfg =
    full != null &&
    thr != null &&
    Number.isFinite(Number(full)) &&
    Number(full) > 0 &&
    Number.isFinite(Number(thr));
  const fullN = hasCfg ? Number(full) : NaN;
  const thrN = hasCfg ? Number(thr) : NaN;
  const minRowsExact = hasCfg ? (fullN * thrN) / 100 : NaN;
  const minRowsDisplay = Number.isFinite(minRowsExact) ? Math.round(minRowsExact) : NaN;

  return (
    <Box sx={{ maxWidth: 320, py: 0.25 }}>
      {statusKey === 'scheduled' && (
        <Typography variant="caption" display="block" sx={{ mb: 0.75 }}>
          Run date is <strong>today or in the future</strong> (UTC). No pass/fail until that calendar day has
          finished.
        </Typography>
      )}
      {(statusKey === 'success' || statusKey === 'failed') && (
        <Typography variant="caption" display="block" sx={{ mb: 0.75 }}>
          Run date is <strong>in the past</strong> (UTC). Compared <code>total_count</code> (calendar rows) to the
          configured full total and threshold.
        </Typography>
      )}
      {statusKey === 'not_applicable' && (
        <Typography variant="caption" display="block" sx={{ mb: 0.75 }}>
          No denominator/threshold in config for this process name.
        </Typography>
      )}
      {hasCfg ? (
        <>
          <Typography variant="caption" display="block">
            Full total (denominator): <strong>{formatIntHuman(fullN)}</strong>
          </Typography>
          <Typography variant="caption" display="block">
            Pass threshold: <strong>{thrN}%</strong> — success when{' '}
            <strong>total_count ≥ {formatIntHuman(minRowsDisplay)}</strong> (i.e. ≥ {thrN}% of full total).
          </Typography>
        </>
      ) : (
        <Typography variant="caption" display="block" color="text.secondary">
          Config fields not returned for this row; criteria unavailable.
        </Typography>
      )}
    </Box>
  );
}

const DataProcess = ({ adminPassword, displayTimeZone }: DataProcessProps) => {
  const [rows, setRows] = useState<DataProcessRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>('2026-04');
  const [selectedYear, setSelectedYear] = useState<string>('2026');
  const [viewPeriod, setViewPeriod] = useState<DataProcessPeriod>('month');

  const load = useCallback(async () => {
    if (!adminPassword) {
      setRows([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const q =
      viewPeriod === 'month'
        ? { month: selectedMonth, period: 'month' }
        : { year: selectedYear, period: viewPeriod };
    const res = await fetchDataProcessAdmin(adminPassword, q);
    setLoading(false);
    if (res.ok) {
      setRows(res.data.rows || []);
    } else {
      setRows([]);
      setError(res.error);
    }
  }, [adminPassword, selectedMonth, selectedYear, viewPeriod]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo((): GridColDef[] => {
    const tzShort = shortTzLabel(displayTimeZone);
    return [
      {
        field: 'run_date',
        headerName: 'Date',
        width: 130,
      },
      {
        field: 'process_name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 200,
        renderCell: (params: GridRenderCellParams<DataProcessRow>) => {
          const row = params.row;
          const label = String(params.value ?? '');
          const key = label.toLowerCase();
          const tooltipTitle = statusTooltipContent(row, key);

          let icon: React.ReactNode;
          if (key === 'success') {
            icon = <CheckCircleRoundedIcon sx={{ fontSize: 22, color: STATUS_SUCCESS }} aria-hidden />;
          } else if (key === 'failed') {
            icon = <CancelRoundedIcon sx={{ fontSize: 22, color: STATUS_FAILED }} aria-hidden />;
          } else if (key === 'scheduled') {
            icon = <ScheduleRoundedIcon sx={{ fontSize: 22, color: STATUS_SCHEDULED }} aria-hidden />;
          } else {
            icon = <HelpOutlineRoundedIcon sx={{ fontSize: 22, color: STATUS_NEUTRAL }} aria-hidden />;
          }

          return (
            <Tooltip title={tooltipTitle} arrow enterDelay={400}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 0,
                  cursor: 'help',
                  py: 0.25,
                }}
              >
                {icon}
                <Typography variant="body2" sx={{ color: COLOR_TEXT_2, textTransform: 'none' }} noWrap>
                  {label}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'calendar_fill_percent',
        headerName: 'Fill %',
        width: 120,
        type: 'number',
        valueGetter: (params: GridValueGetterParams<DataProcessRow>) => {
          const row = params.row;
          const denom = row.full_total_count == null ? 0 : Number(row.full_total_count);
          const num = Number(row.total_count || 0);
          if (!Number.isFinite(denom) || denom <= 0) return null;
          return (num / denom) * 100;
        },
        valueFormatter: params => {
          const v = params.value as number | null;
          if (v == null || !Number.isFinite(Number(v))) return 'N/A';
          return `${Number(v).toFixed(2)}%`;
        },
      },
      {
        field: 'total_count',
        headerName: 'Total count',
        width: 130,
        type: 'number',
      },
      {
        field: 'updated_at',
        headerName: `Updated date (${tzShort})`,
        width: 220,
        valueFormatter: params => {
          const v = String(params.value || '');
          if (!v) return '';
          return parseUtcSignupStringToDisplay(v, displayTimeZone);
        },
      },
    ];
  }, [displayTimeZone]);

  const periodSummary = useMemo(() => {
    if (viewPeriod === 'month') return selectedMonth;
    if (viewPeriod === 'year') return `${selectedYear} (full year)`;
    return `${selectedYear} ${viewPeriod.toUpperCase()}`;
  }, [viewPeriod, selectedMonth, selectedYear]);

  const handlePeriodChange = (e: SelectChangeEvent<DataProcessPeriod>) => {
    const next = e.target.value as DataProcessPeriod;
    setViewPeriod(next);
    if (next !== 'month') {
      setSelectedYear(yearFromMonthInput(selectedMonth));
    }
  };

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          maxWidth: 1200,
          mx: 'auto',
          border: `1px solid ${COLOR_GREY_200}`,
          borderRadius: 2,
          bgcolor: COLOR_BACKGROUND_2,
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
        }}
      >
        <Typography variant="h5" sx={{ color: COLOR_TEXT_2, fontWeight: 600, mb: 0.25 }}>
          Data Process
        </Typography>
        <Typography variant="body2" sx={{ color: COLOR_TEXT_3, mb: 2 }}>
          Monitor scheduled process rows from <code>Logs.data_process</code>. Status and fill reflect config rules
          and calendar row counts (UTC date vs today).
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="data-process-range-label">Range</InputLabel>
            <Select<DataProcessPeriod>
              labelId="data-process-range-label"
              label="Range"
              value={viewPeriod}
              onChange={handlePeriodChange}
            >
              <MenuItem value="month">Single month</MenuItem>
              <MenuItem value="year">Full year</MenuItem>
              <MenuItem value="q1">Q1 (Jan–Mar)</MenuItem>
              <MenuItem value="q2">Q2 (Apr–Jun)</MenuItem>
              <MenuItem value="q3">Q3 (Jul–Sep)</MenuItem>
              <MenuItem value="q4">Q4 (Oct–Dec)</MenuItem>
            </Select>
          </FormControl>
          {viewPeriod === 'month' ? (
            <TextField
              size="small"
              label="Month"
              type="month"
              value={selectedMonth}
              onChange={e => {
                const v = String(e.target.value || '');
                setSelectedMonth(v);
                setSelectedYear(yearFromMonthInput(v));
              }}
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <TextField
              size="small"
              label="Year"
              type="number"
              inputProps={{ min: 2000, max: 2100 }}
              value={selectedYear}
              onChange={e => setSelectedYear(String(e.target.value || '').slice(0, 4))}
              sx={{ width: 120 }}
              InputLabelProps={{ shrink: true }}
            />
          )}
          <Typography variant="body2" sx={{ color: COLOR_TEXT_3 }}>
            Showing: <strong>{periodSummary}</strong> · oldest dates first
          </Typography>
        </Box>

        {!adminPassword && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter admin password to load data process logs.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: COLOR_PRIMARY_1 }} />
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: 560,
              border: `1px solid ${COLOR_GREY_200}`,
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: TABLE_BG,
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={100}
              rowsPerPageOptions={[25, 50, 100]}
              disableSelectionOnClick
              density="compact"
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: TABLE_HEADER_BG,
                  fontWeight: 600,
                },
                '& .MuiDataGrid-main': { backgroundColor: TABLE_BG },
              }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DataProcess;
